import React, { useCallback } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { css } from '@linaria/core';
import { useAtom } from 'jotai';
import useYup from '../../hooks/useYup';
import { originCountryAtom, destinationCountryAtom } from '../../contexts/shipment';
import { Card, Icon, Skeleton, FormControl, breakpoints, colors } from '@spaceshipapp/cambridge';
import Autocomplete from '../../components/Autocomplete';
import * as analytics from '../../analytics/quotation';
import { useCountries } from '../../hooks/useCountries';
import { useCountryAutocomplete } from '../../hooks/useCountryAutocomplete';

interface Props {
  errors?: ReturnType<typeof useYup>['errors'];
}

const CountrySelect: React.FC<Props> = ({ errors }) => {
  const { t, lang } = useTranslation('common');
  const [originCountry, setOriginCountry] = useAtom(originCountryAtom);
  const [destinationCountry, setDestinationCountry] = useAtom(destinationCountryAtom);
  const { data, isLoading: isLoadingCountries } = useCountries();
  const countries = data.map((country) => {
    return { ...country, label: t(`countries:Country.${country.alpha2}`) };
  });

  const originCountryAutocompleteProps = useCountryAutocomplete({
    lang,
    id: 'origin',
    isError: !!errors?.originCountry,
    label: t('CountrySelect.labelFrom'),
    noItemsLabel: t('CountrySelect.notFound'),
    placeholder: t('CountrySelect.placeholder'),
    countries: countries,
    selectedCountryAlpha2: originCountry,
    onChange: useCallback(
      (countryAlpha2) => {
        setOriginCountry(countryAlpha2 ?? '');

        if (countryAlpha2) {
          analytics.sendOriginRegionChanged(countryAlpha2);
        }
      },
      [setOriginCountry],
    ),
  });

  const destinationCountryAutocompleteProps = useCountryAutocomplete({
    lang,
    id: 'destination',
    isError: !!errors?.destinationCountry,
    label: t('CountrySelect.labelTo'),
    noItemsLabel: t('CountrySelect.notFound'),
    placeholder: t('CountrySelect.placeholder'),
    countries: countries,
    selectedCountryAlpha2: destinationCountry,
    onChange: useCallback((countryAlpha2) => {
      setDestinationCountry(countryAlpha2 ?? '');

      if (countryAlpha2) {
        analytics.sendDestinationRegionChanged(countryAlpha2);
      }
    }, []),
  });

  return (
    <Card className={cardStyle}>
      <div>
        <FormControl className={formControlStyle}>
          <Skeleton width="100%" height="3.125rem" isLoaded={!isLoadingCountries}>
            <Autocomplete {...originCountryAutocompleteProps} />
          </Skeleton>
        </FormControl>
        <Icon.Flight width={24} height={24} className={iconStyle} />
      </div>
      <div>
        <FormControl
          className={formControlStyle}
          errorMessage={errors?.destinationCountry && t(errors?.destinationCountry as string)}
        >
          <Skeleton width="100%" height="3.125rem" isLoaded={!isLoadingCountries}>
            <Autocomplete {...destinationCountryAutocompleteProps} />
          </Skeleton>
        </FormControl>
      </div>
    </Card>
  );
};

const cardStyle = css`
  position: relative;
  width: 100%;
  display: flex;
  flex-flow: column nowrap;
  padding: 0;

  > * {
    width: 100%;
    padding: 1rem;

    &:not(:last-child) {
      border-bottom: 1px solid ${colors.border};
    }
  }

  @media (min-width: ${breakpoints.lg}) {
    flex-flow: row nowrap;

    > * {
      padding: 1.5rem;

      &:not(:last-child) {
        border-bottom: 0;
        border-right: 1px solid ${colors.border};
      }
    }
  }
`;

const formControlStyle = css`
  width: 100%;
`;

const iconStyle = css`
  display: none;

  @media (min-width: ${breakpoints.lg}) {
    display: block;
    position: absolute;
    top: calc(50% - 12px);
    left: calc(50% - 12px);
  }
`;

export default CountrySelect;
